// migrate-wasm.ts — one-time, NON-DESTRUCTIVE migration of the WASM client's
// persistent state into the TS client's stores, so switching a player to /ts/
// (or defaulting them onto it) does not lose campaign progress, saves or
// settings.
//
// THE WASM SIDE (ground-truthed against the shipped prod build,
// test/vectors/migration/idbfs-groundtruth.json): the emscripten client mounts
// IDBFS at /persistent (game/sdl/web/hosthelpers.cpp:33-57) — an IndexedDB
// database NAMED '/persistent' (version 21) with one object store 'FILE_DATA'
// (+ a 'timestamp' index), keyed by absolute path, each value
// { contents?: Uint8Array, mode: number, timestamp: Date } (directories carry
// no contents). The paths that matter:
//   /persistent/prefs.json                      preferences.cpp's JSON document
//   /persistent/SaveGames/htsave<n>_<name>.bin  Game::SaveGame envelope+tail
//                                               (':' in <name> mangled to '#',
//                                               sdl/savegame.cpp:243-250);
//                                               slot -3 = the Reinitialize
//                                               autosave; htsavetemp /
//                                               httempsave.bin = crash débris
//   /persistent/Completes/kkkkkkkk-hhhh…32      zero-length completion marks
//                                               (completemanager.cpp:26-48)
//   /persistent/MissionPacks/, MissionPackInfos/ the DLC caches (PackManager
//                                               filenames, byte-verbatim)
//
// RULES (run once, marker db 'wi.ts.migration'):
//   prefs     — imported only when the TS store holds NO document. The TS
//               document, when present, reflects the player's CURRENT choices
//               in the TS client (the localStorage doc carries no timestamp to
//               compare, and clobbering live settings is strictly worse).
//               The WASM bytes are imported VERBATIM (same Stream byte format:
//               the file IS the persisted artifact; parsing happens at load).
//   saves     — per slot, NEWEST WINS by date: both sides are genuine user
//               saves in the same slot namespace, and the newest one is the
//               player's latest progress (the C++ itself ranks slots by file
//               mtime — loadsave.cpp LoadGameForm::SelectLast). The WASM file
//               mtime rides the IDBFS record's timestamp; bytes byte-verbatim.
//   completes — union: zero-length marker files are monotone (a completed
//               mission stays completed), so merging is exact.
//   packs     — byte-verbatim copy into the TS pack caches, existing TS
//               entries win (same filenames both sides — PackManager's
//               "iiiiiiii-hhhh…32"; InitFromInstalled re-validates, exactly
//               as the C++ readdir did).
// A corrupt artifact is skipped with a console warning; nothing ever aborts
// the boot; the WASM database is NEVER written or deleted.

import type { PrefStore } from '../io/prefs.ts';
import { PrefsFromFile } from '../io/prefs.ts';
import type { SaveStore } from '../io/savegame.ts';
import { PlaySavedGame, knGameReinitializeSave } from '../io/savegame.ts';
import type { CompleteStore } from '../io/completemgr.ts';
import { MemoryStream } from '../../../sim/src/sm/stream.ts';
import { IdbPrefStore, IdbCompleteStore, IdbSaveStore, idbOpenStoreDb } from './stores.ts';

// game/sdl/web/hosthelpers.cpp:23,52-57 — the IDBFS mountpoint IS the database
// name, and the persistent paths under it
export const kszWasmDb = '/persistent';
export const kszWasmPrefsPath = '/persistent/prefs.json';
export const kszWasmSavesDir = '/persistent/SaveGames/';
export const kszWasmCompletesDir = '/persistent/Completes/';
export const kszWasmPacksDir = '/persistent/MissionPacks/';
export const kszWasmPackInfosDir = '/persistent/MissionPackInfos/';

// emscripten IDBFS schema (the shipped build's library_idbfs: DB_STORE_NAME)
export const kszIdbfsStore = 'FILE_DATA';

/** One IDBFS file record, normalized (directories are filtered out before this). */
export interface IdbfsRecord {
  path: string;
  bytes: Uint8Array;
  /** the IDBFS record's timestamp (the POSIX mtime), epoch ms */
  timestampMs: number;
}

export interface MigrationTargets {
  prefStore: PrefStore;
  saveStore: SaveStore;
  completeStore: CompleteStore;
  /** optional sinks for the DLC pack / pack-info caches (filename → bytes);
   *  the sink owns its overwrite policy (openPersistentStores: existing wins) */
  packSink?: (filename: string, bytes: Uint8Array) => void;
  packInfoSink?: (filename: string, bytes: Uint8Array) => void;
}

export interface MigrationSummary {
  prefs: boolean;
  saves: number;
  completes: number;
  packs: number;
  packInfos: number;
  skipped: { path: string; reason: string }[];
}

// game/sdl/savegame.cpp:115/241 — "htsave%d_%s.bin"; the slot is any int
// (-3 = Reinitialize), the name is the level title with ':' mangled to '#'
const reSaveFilename = /^htsave(-?\d+)_(.*)\.bin$/s;

// completemanager.cpp:26-46 ParseFilename — 8 hex chars, '-', 32 hex chars
const reCompleteFilename = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{32}$/;

/** Validate + apply one batch of IDBFS file records to the TS stores (pure,
 *  synchronous — the IndexedDB plumbing lives in migrateWasmPersistence).
 *  Per-artifact failures skip with a warning; never throws. */
export function applyWasmRecords(
  records: IdbfsRecord[],
  targets: MigrationTargets,
): MigrationSummary {
  const sum: MigrationSummary = {
    prefs: false,
    saves: 0,
    completes: 0,
    packs: 0,
    packInfos: 0,
    skipped: [],
  };
  const skip = (path: string, reason: string): void => {
    sum.skipped.push({ path, reason });
    console.warn(`migrate-wasm: skipped ${path} (${reason})`);
  };

  for (const rec of records) {
    try {
      if (rec.path === kszWasmPrefsPath) {
        // verbatim document bytes; parse only to VALIDATE (InitFromFile's
        // JSON-object gate), never re-encode
        if (targets.prefStore.read() !== null) {
          skip(rec.path, 'TS prefs already exist — kept');
          continue;
        }
        const json = new TextDecoder().decode(rec.bytes);
        if (PrefsFromFile({ read: () => json, write: () => true }) === null) {
          skip(rec.path, 'unparseable prefs document');
          continue;
        }
        targets.prefStore.write(json);
        sum.prefs = true;
      } else if (rec.path.startsWith(kszWasmSavesDir)) {
        const m = reSaveFilename.exec(rec.path.slice(kszWasmSavesDir.length));
        if (m === null) {
          // htsavetemp / httempsave.bin crash débris, or stray files
          skip(rec.path, 'not a save slot file');
          continue;
        }
        const nGame = parseInt(m[1], 10);
        // Never migrate the Reinitialize/resume autosave (slot -3): it is a TRANSIENT
        // mid-mission snapshot the WASM client rewrites every 500ms, not a user save.
        // Importing it makes the TS client resume whatever mission the player last had
        // open in the WASM client — i.e. switching to /ts (or a refresh) drops them back
        // in-game. The real saved games (slots 0..19) still migrate.
        if (nGame === knGameReinitializeSave) {
          skip(rec.path, 'reinitialize/resume autosave — not migrated (transient)');
          continue;
        }
        const szName = m[2].replace(/#/g, ':'); // sdl/savegame.cpp:216-221
        // the envelope must parse AND the reads must not have run off the end
        const pstm = new MemoryStream(rec.bytes);
        if (PlaySavedGame(pstm) === null || !pstm.IsSuccess()) {
          skip(rec.path, 'stale or corrupt savegame');
          continue;
        }
        const existing = targets.saveStore.get(nGame);
        if (existing !== null && existing.msDate >= rec.timestampMs) {
          skip(rec.path, `TS slot ${nGame} is newer — kept`);
          continue;
        }
        targets.saveStore.put({
          nGame,
          szName,
          msDate: rec.timestampMs,
          bytes: rec.bytes.slice(),
        });
        sum.saves++;
      } else if (rec.path.startsWith(kszWasmCompletesDir)) {
        const filename = rec.path.slice(kszWasmCompletesDir.length);
        if (!reCompleteFilename.test(filename)) {
          skip(rec.path, 'not a completion marker');
          continue;
        }
        targets.completeStore.create(filename); // union — create is idempotent
        sum.completes++;
      } else if (rec.path.startsWith(kszWasmPacksDir)) {
        if (targets.packSink !== undefined) {
          targets.packSink(rec.path.slice(kszWasmPacksDir.length), rec.bytes);
          sum.packs++;
        }
      } else if (rec.path.startsWith(kszWasmPackInfosDir)) {
        if (targets.packInfoSink !== undefined) {
          targets.packInfoSink(rec.path.slice(kszWasmPackInfosDir.length), rec.bytes);
          sum.packInfos++;
        }
      }
      // anything else (/persistent/tmp/…) is transient — ignore silently
    } catch (e) {
      skip(rec.path, `exception: ${e}`);
    }
  }
  return sum;
}

/** Open an IndexedDB database ONLY if it already exists: a version-less open
 *  that aborts the upgrade transaction when it would CREATE the database
 *  (onupgradeneeded with oldVersion 0), leaving no trace. */
function idbOpenExisting(idb: IDBFactory, name: string): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = idb.open(name);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      try {
        req.transaction?.abort();
      } catch {
        /* already aborting */
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null); // AbortError (didn't exist) or unavailable
    req.onblocked = () => resolve(null);
  });
}

/** Read every FILE record out of the WASM client's IDBFS database. Returns
 *  null when the database (or its FILE_DATA store) does not exist. */
export async function readWasmIdbfs(
  idb: IDBFactory,
  dbName = kszWasmDb,
): Promise<IdbfsRecord[] | null> {
  const db = await idbOpenExisting(idb, dbName);
  if (db === null) return null;
  try {
    if (!db.objectStoreNames.contains(kszIdbfsStore)) return null;
    const { keys, values } = await new Promise<{
      keys: IDBValidKey[];
      values: unknown[];
    }>((resolve, reject) => {
      const tx = db.transaction(kszIdbfsStore, 'readonly');
      const os = tx.objectStore(kszIdbfsStore);
      const reqK = os.getAllKeys();
      const reqV = os.getAll();
      tx.oncomplete = () => resolve({ keys: reqK.result, values: reqV.result });
      tx.onerror = () => reject(tx.error ?? new Error('IDBFS read failed'));
    });
    const records: IdbfsRecord[] = [];
    for (let i = 0; i < keys.length; i++) {
      const path = keys[i];
      const v = values[i] as { contents?: unknown; timestamp?: unknown } | null;
      if (typeof path !== 'string' || v === null || typeof v !== 'object') continue;
      const bytes =
        v.contents instanceof Uint8Array
          ? v.contents
          : v.contents instanceof ArrayBuffer
            ? new Uint8Array(v.contents)
            : ArrayBuffer.isView(v.contents)
              ? new Uint8Array(v.contents.buffer, v.contents.byteOffset, v.contents.byteLength)
              : null;
      if (bytes === null) continue; // directories carry no contents
      const ts = v.timestamp;
      const timestampMs = ts instanceof Date ? ts.getTime() : typeof ts === 'number' ? ts : 0;
      records.push({ path, bytes, timestampMs });
    }
    return records;
  } finally {
    db.close();
  }
}

// ---- the run-once marker ------------------------------------------------------

const kszMarkerDb = 'wi.ts.migration';
const kszMarkerStore = 'meta';
const kszMarkerKey = 'wasm';

async function readMarker(): Promise<unknown> {
  const db = await idbOpenStoreDb(kszMarkerDb, kszMarkerStore);
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(kszMarkerStore, 'readonly');
      const req = tx.objectStore(kszMarkerStore).get(kszMarkerKey);
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error ?? new Error('marker read failed'));
    });
  } finally {
    db.close();
  }
}

async function writeMarker(value: unknown): Promise<void> {
  const db = await idbOpenStoreDb(kszMarkerDb, kszMarkerStore);
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(kszMarkerStore, 'readwrite');
      tx.objectStore(kszMarkerStore).put(value, kszMarkerKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('marker write failed'));
    });
  } finally {
    db.close();
  }
}

/** The one-time WASM→TS migration: no-op when already done, when IndexedDB is
 *  unavailable, or when no WASM database exists (the marker is still written
 *  so the probe doesn't repeat). Returns the summary, or null when it didn't
 *  run. Never throws. */
export async function migrateWasmPersistence(
  targets: MigrationTargets,
  opts: { wasmDbName?: string } = {},
): Promise<MigrationSummary | null> {
  let idb: IDBFactory;
  try {
    idb = indexedDB;
  } catch {
    return null; // no IndexedDB host (tests/privacy) — nothing to migrate from
  }

  try {
    if ((await readMarker()) !== undefined) return null; // already ran
  } catch (e) {
    console.warn('migrate-wasm: marker unreadable — skipping migration:', e);
    return null;
  }

  let summary: MigrationSummary | null = null;
  try {
    const records = await readWasmIdbfs(idb, opts.wasmDbName ?? kszWasmDb);
    if (records !== null) {
      summary = applyWasmRecords(records, targets);
      console.info(
        'migrate-wasm: imported WASM client state:',
        JSON.stringify({
          prefs: summary.prefs,
          saves: summary.saves,
          completes: summary.completes,
          packs: summary.packs,
          packInfos: summary.packInfos,
          skipped: summary.skipped.length,
        }),
      );
    }
  } catch (e) {
    // transient IDB failure: DON'T mark done — retry next boot
    console.warn('migrate-wasm: WASM state read failed — will retry next boot:', e);
    return null;
  }

  try {
    await writeMarker({
      when: new Date().toISOString(),
      found: summary !== null,
      ...(summary !== null
        ? {
            prefs: summary.prefs,
            saves: summary.saves,
            completes: summary.completes,
            packs: summary.packs,
            packInfos: summary.packInfos,
          }
        : {}),
    });
  } catch (e) {
    console.warn('migrate-wasm: marker write failed (migration may re-run):', e);
  }
  return summary;
}

// ---- the boot entry point -------------------------------------------------------

/** A pack-cache sink writing straight into the IdbPackStore database shape
 *  ('files' store, filename keys) BEFORE IdbPackStore.open hydrates it later
 *  in boot. Existing entries win. Returns undefined when IDB is unavailable. */
async function rawPackSink(
  dbName: string,
): Promise<((filename: string, bytes: Uint8Array) => void) | undefined> {
  try {
    const db = await idbOpenStoreDb(dbName, 'files');
    const existing = await new Promise<Set<IDBValidKey>>((resolve, reject) => {
      const tx = db.transaction('files', 'readonly');
      const req = tx.objectStore('files').getAllKeys();
      tx.oncomplete = () => resolve(new Set(req.result));
      tx.onerror = () => reject(tx.error ?? new Error('pack keys read failed'));
    });
    return (filename, bytes) => {
      if (existing.has(filename)) return;
      try {
        db.transaction('files', 'readwrite').objectStore('files').put(bytes.slice(), filename);
      } catch (e) {
        console.warn(`migrate-wasm: pack write ${filename} failed:`, e);
      }
    };
  } catch {
    return undefined;
  }
}

/** Boot-time store construction for web/main.ts: opens the three IndexedDB
 *  stores (each migrating its legacy localStorage document once) and runs the
 *  one-time WASM IDBFS migration over them. Replaces the direct
 *  IdbSaveStore.open() + LocalPrefStore/LocalCompleteStore wiring. */
export async function openPersistentStores(): Promise<{
  prefStore: IdbPrefStore;
  saveStore: IdbSaveStore;
  completeStore: IdbCompleteStore;
}> {
  const prefStore = await IdbPrefStore.open();
  const saveStore = await IdbSaveStore.open();
  const completeStore = await IdbCompleteStore.open();
  try {
    await migrateWasmPersistence({
      prefStore,
      saveStore,
      completeStore,
      packSink: await rawPackSink('wi.ts.packs'),
      packInfoSink: await rawPackSink('wi.ts.packinfos'),
    });
  } catch (e) {
    console.warn('migrate-wasm: migration failed (boot continues):', e);
  }
  return { prefStore, saveStore, completeStore };
}
